import React, { useCallback, useEffect, useRef, useState } from "react";
import { MediaVideo } from "./WorkMedia";
import useCanHover from "../../hooks/useCanHover";

// Click to zoom, the way a browser's own image viewer does it. Images and
// videos in the dialog share this: one click enlarges the artwork around the
// point clicked, and while it is enlarged it can be dragged around. The next
// click puts it back exactly where it was.
//
// This is a mouse feature. On a touch screen the browser already has a better
// zoom — a pinch, with the fingers deciding both scale and place — and a tap
// that enlarges to a fixed step only gets in its way, so there the artwork is
// rendered plain (StaticMedia) and the dialog's own pinch guard lets the
// browser take it. The split is on `(hover: hover)`, the pointer's capability,
// not on viewport width.
//
// Every item is zoomable. A screenshot larger than the dialog opens out toward
// its own pixels (capped below); a small diagram or a clip that already fits
// still enlarges by a fixed step, so the same click-and-drag works everywhere
// rather than only on the heaviest files.
//
// Panning belongs to the zoomed state alone. A drag on a fitted item does
// nothing at all, which is what leaves a swipe free to change gallery screen:
// the dialog skips its own swipe handling only while the artwork is zoomed.

const DRAG_SLOP = 4;

// Small artwork still gets a zoom, so a 1024-wide diagram in a 1216-wide
// dialog is not left without one.
const MIN_ZOOM = 2;

// As far as a zoom goes, even when the file has more pixels to give. A large
// screenshot at its natural size overshoots what is comfortable to read and pan
// around, so the enlargement stops here.
const MAX_ZOOM = 2.5;

function intrinsicSize(el, type) {
  if (!el) return null;
  if (type === "video") {
    if (!el.videoWidth) return null;
    return { width: el.videoWidth, height: el.videoHeight };
  }
  if (!el.naturalWidth) return null;
  return { width: el.naturalWidth, height: el.naturalHeight };
}

// The touch-screen form: the same element with none of the pointer handling.
// It still swallows the click, so a tap on the artwork does not close the
// dialog behind it, and still carries `data-zoomed` so the dialog's swipe
// guard has the same attribute to read on either form.
function StaticMedia({ src, alt, type, className, style }) {
  const props = {
    className,
    style,
    "data-zoomed": "false",
    onClick: (event) => event.stopPropagation(),
  };
  if (type === "video") return <MediaVideo src={src} {...props} />;
  return <img src={src} alt={alt} draggable={false} decoding="async" {...props} />;
}

function ZoomableImage(props) {
  const canHover = useCanHover();
  return canHover ? <ZoomableMedia {...props} /> : <StaticMedia {...props} />;
}

function ZoomableMedia({ src, alt, type = "image", className = "", style }) {
  const mediaRef = useRef(null);
  // The artwork as the dialog fits it — the geometry every zoom is measured
  // against, so it is only ever read while nothing is scaled.
  const fitted = useRef({ width: 0, height: 0, centerX: 0, centerY: 0 });
  const press = useRef(null);

  const [scale, setScale] = useState(MIN_ZOOM);
  const [zoomed, setZoomed] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const [measureTick, setMeasureTick] = useState(0);

  // The fitted geometry is worked out rather than measured. Measuring the media
  // would mean reading a rect that carries whatever transform is on it — and a
  // zoom eases back over 180ms, so a reading taken just after zooming out still
  // reports the enlarged size and the item would appear to have nothing left to
  // show. The parent is never scaled, and `object-contain` on media with auto
  // width and height fits the natural ratio inside it, so the same box can be
  // derived from the parent and the file's own dimensions at any moment.
  const readFit = useCallback(() => {
    const media = mediaRef.current;
    const box = media?.parentElement?.getBoundingClientRect();
    const size = intrinsicSize(media, type);
    if (!size || !box?.width || !box?.height) return null;

    const ratio = size.width / size.height;
    let width = box.width;
    let height = width / ratio;
    if (height > box.height) {
      height = box.height;
      width = height * ratio;
    }
    return {
      width,
      height,
      centerX: box.left + box.width / 2,
      centerY: box.top + box.height / 2,
    };
  }, [type]);

  const reset = useCallback(() => {
    setZoomed(false);
    setOffset({ x: 0, y: 0 });
  }, []);

  // A new file always arrives fitted.
  useEffect(() => {
    reset();
  }, [src, reset]);

  useEffect(() => {
    const fit = readFit();
    const size = intrinsicSize(mediaRef.current, type);
    if (!fit || !size) return;
    fitted.current = fit;
    setScale(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, size.width / fit.width)));
  }, [measureTick, readFit, type]);

  // A resize changes the fit the zoom was measured against, so it drops back to
  // it and takes a fresh reading.
  useEffect(() => {
    const onResize = () => {
      reset();
      setMeasureTick((tick) => tick + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reset]);

  // How far the artwork may be dragged before an edge would pull inside the
  // window and leave a gap. An axis with nothing to spare does not move at all.
  const clampOffset = useCallback((next, activeScale) => {
    const { width, height, centerX, centerY } = fitted.current;
    const axis = (center, half, viewport) => {
      const min = viewport - center - half;
      const max = half - center;
      return min > max ? null : { min, max };
    };
    const horizontal = axis(centerX, (width * activeScale) / 2, window.innerWidth);
    const vertical = axis(centerY, (height * activeScale) / 2, window.innerHeight);
    return {
      x: horizontal ? Math.min(Math.max(next.x, horizontal.min), horizontal.max) : 0,
      y: vertical ? Math.min(Math.max(next.y, vertical.min), vertical.max) : 0,
    };
  }, []);

  // Zooming holds the point under the pointer still: scaling about the centre
  // would otherwise throw it (1 - scale) of its distance from that centre. The
  // clamp then pulls the artwork back if that would have opened a gap at an edge.
  const anchor = useCallback(
    (clientX, clientY, activeScale) => {
      const { centerX, centerY } = fitted.current;
      return clampOffset(
        {
          x: (clientX - centerX) * (1 - activeScale),
          y: (clientY - centerY) * (1 - activeScale),
        },
        activeScale
      );
    },
    [clampOffset]
  );

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    press.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
      dragged: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const active = press.current;
    if (!active || active.id !== event.pointerId) return;
    const dx = event.clientX - active.x;
    const dy = event.clientY - active.y;
    if (!active.dragged && Math.hypot(dx, dy) > DRAG_SLOP) active.dragged = true;
    if (!active.dragged || !zoomed) return;
    setPanning(true);
    setOffset(clampOffset({ x: active.offsetX + dx, y: active.offsetY + dy }, scale));
  };

  const onPointerUp = (event) => {
    const active = press.current;
    press.current = null;
    setPanning(false);
    if (!active || active.id !== event.pointerId) return;
    if (active.dragged) return;
    if (zoomed) {
      reset();
      return;
    }
    fitted.current = readFit() || fitted.current;
    setZoomed(true);
    setOffset(anchor(event.clientX, event.clientY, scale));
  };

  const abandonPress = () => {
    press.current = null;
    setPanning(false);
  };

  const mediaStyle = {
    ...style,
    cursor: panning ? "grabbing" : zoomed ? "zoom-out" : "zoom-in",
    transform: zoomed ? `translate(${offset.x}px, ${offset.y}px) scale(${scale})` : undefined,
    transition: panning ? "none" : "transform 180ms ease-out",
    touchAction: zoomed ? "none" : undefined,
    willChange: zoomed ? "transform" : undefined,
  };

  const mediaProps = {
    ref: mediaRef,
    className: `select-none ${className}`,
    style: mediaStyle,
    "data-zoomed": zoomed ? "true" : "false",
    onLoadedMetadata: () => setMeasureTick((tick) => tick + 1),
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: abandonPress,
    onClick: (event) => event.stopPropagation(),
  };

  if (type === "video") {
    return <MediaVideo src={src} {...mediaProps} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      decoding="async"
      onLoad={() => setMeasureTick((tick) => tick + 1)}
      onDragStart={(event) => event.preventDefault()}
      {...mediaProps}
    />
  );
}

export default ZoomableImage;
